/**
 * Avatar Picker Component
 *
 * Generates personalized avatars from user name with
 * style and color customization, plus custom upload.
 */

import { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Button } from './ui/button';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Upload, Image as ImageIcon, Check, User } from 'lucide-react';
import { AVATAR_STYLES, AVATAR_COLORS, generateAvatarUrl, getInitials } from '@/lib/avatar-presets';
import type { AvatarStyle, AvatarColor } from '@/lib/avatar-presets';
import { cn } from '@/lib/utils';

interface AvatarPickerProps {
  currentAvatarUrl?: string | null;
  onAvatarChange: (avatarUrl: string, isUpload: boolean) => void;
  userName?: string;
  trigger?: React.ReactNode;
}

export function AvatarPicker({ currentAvatarUrl, onAvatarChange, userName, trigger }: AvatarPickerProps) {
  const [open, setOpen] = useState(false);
  const [selectedStyle, setSelectedStyle] = useState<AvatarStyle>(AVATAR_STYLES[0]);
  const [selectedColor, setSelectedColor] = useState<AvatarColor>(AVATAR_COLORS[1]);
  const [uploadPreview, setUploadPreview] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('generate');
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const displayName = userName || 'User';
  const generatedUrl = generateAvatarUrl(displayName, selectedStyle, selectedColor);

  const currentSelection = activeTab === 'upload' ? uploadPreview : generatedUrl;

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be smaller than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setUploadPreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (activeTab === 'upload' && uploadPreview) {
      setIsUploading(true);
      try {
        const response = await fetch(uploadPreview);
        const blob = await response.blob();

        const formData = new FormData();
        formData.append('avatar', blob, 'avatar.jpg');

        const uploadResponse = await fetch('/api/upload/avatar', {
          method: 'POST',
          body: formData,
          credentials: 'include',
        });

        if (!uploadResponse.ok) {
          throw new Error('Upload failed');
        }

        const { url } = await uploadResponse.json();
        onAvatarChange(url, true);
        setOpen(false);
      } catch (error) {
        console.error('Upload error:', error);
        alert('Failed to upload image. Please try again.');
      } finally {
        setIsUploading(false);
      }
    } else if (activeTab === 'generate') {
      onAvatarChange(generatedUrl, false);
      setOpen(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm">
            <ImageIcon className="mr-2 h-4 w-4" />
            Change Avatar
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-lg">Profile Avatar</DialogTitle>
          <DialogDescription>
            Customize your avatar appearance
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="generate" className="text-sm">
              <User className="mr-2 h-3.5 w-3.5" />
              Generate
            </TabsTrigger>
            <TabsTrigger value="upload" className="text-sm">
              <Upload className="mr-2 h-3.5 w-3.5" />
              Upload
            </TabsTrigger>
          </TabsList>

          {/* Generate Tab */}
          <TabsContent value="generate" className="space-y-5 mt-4">
            {/* Live Preview */}
            <div className="flex flex-col items-center gap-2">
              <div className="rounded-full p-1 ring-2 ring-border">
                <Avatar className="h-24 w-24">
                  <AvatarImage src={generatedUrl} alt="Avatar preview" />
                  <AvatarFallback className="text-2xl font-semibold">
                    {getInitials(displayName)}
                  </AvatarFallback>
                </Avatar>
              </div>
              <span className="text-sm font-medium text-muted-foreground">{displayName}</span>
            </div>

            {/* Style Selection */}
            <div className="space-y-2.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Style
              </label>
              <div className="grid grid-cols-4 gap-2">
                {AVATAR_STYLES.map((style) => {
                  const previewUrl = generateAvatarUrl(displayName, style, selectedColor);
                  const isSelected = selectedStyle.id === style.id;
                  return (
                    <button
                      key={style.id}
                      onClick={() => setSelectedStyle(style)}
                      className={cn(
                        'relative flex flex-col items-center gap-1.5 rounded-lg border-2 p-2.5 transition-all',
                        'hover:border-primary/50 hover:bg-muted/50',
                        isSelected
                          ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
                          : 'border-border'
                      )}
                    >
                      <Avatar className="h-12 w-12">
                        <AvatarImage src={previewUrl} alt={style.name} />
                        <AvatarFallback className="text-xs">{getInitials(displayName)}</AvatarFallback>
                      </Avatar>
                      <span className={cn(
                        'text-[11px] font-medium',
                        isSelected ? 'text-primary' : 'text-muted-foreground'
                      )}>
                        {style.name}
                      </span>
                      {isSelected && (
                        <div className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-primary flex items-center justify-center">
                          <Check className="h-2.5 w-2.5 text-primary-foreground" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Color Selection */}
            <div className="space-y-2.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Color
              </label>
              <div className="flex gap-2 flex-wrap">
                {AVATAR_COLORS.map((color) => {
                  const isSelected = selectedColor.id === color.id;
                  return (
                    <button
                      key={color.id}
                      onClick={() => setSelectedColor(color)}
                      title={color.name}
                      className={cn(
                        'relative h-8 w-8 rounded-full transition-all',
                        'hover:scale-110 hover:ring-2 hover:ring-offset-2 hover:ring-offset-background',
                        isSelected
                          ? 'ring-2 ring-offset-2 ring-offset-background ring-primary scale-110'
                          : 'ring-1 ring-black/10'
                      )}
                      style={{ backgroundColor: `#${color.hex}` }}
                    >
                      {isSelected && (
                        <Check className="h-3.5 w-3.5 text-white absolute inset-0 m-auto" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </TabsContent>

          {/* Upload Tab */}
          <TabsContent value="upload" className="mt-4">
            <div className="flex flex-col items-center justify-center py-6 space-y-4">
              {uploadPreview ? (
                <div className="space-y-4">
                  <div className="rounded-full p-1 ring-2 ring-border mx-auto w-fit">
                    <Avatar className="h-24 w-24">
                      <AvatarImage src={uploadPreview} alt="Upload preview" />
                    </Avatar>
                  </div>
                  <div className="flex gap-2 justify-center">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setUploadPreview(null);
                        if (fileInputRef.current) {
                          fileInputRef.current.value = '';
                        }
                      }}
                    >
                      Remove
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()}>
                      Replace
                    </Button>
                  </div>
                </div>
              ) : (
                <div
                  className="w-full border-2 border-dashed border-border rounded-lg p-10 text-center cursor-pointer hover:border-primary/50 hover:bg-muted/30 transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="h-10 w-10 mx-auto mb-3 text-muted-foreground/60" />
                  <p className="text-sm font-medium mb-1">Upload a photo</p>
                  <p className="text-xs text-muted-foreground">
                    PNG, JPG or GIF — max 5MB
                  </p>
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileSelect}
              />
            </div>
          </TabsContent>
        </Tabs>

        {/* Action Buttons */}
        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button variant="outline" size="sm" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={
              (activeTab === 'upload' && !uploadPreview) ||
              isUploading
            }
          >
            {isUploading ? 'Uploading...' : 'Save Avatar'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
